import { createAsyncThunk } from '@reduxjs/toolkit';

import { AlteracaoPerfilService } from '../../common/services/alteracao-perfil';
import { BeneficiariosService } from '../../common/services/beneficiarios';
import { ExtratoSaldoContaService } from '../../common/services/extrato-saldo-conta';
import { MemoriaCalculoParticipanteService } from '../../common/services/memoria-calculo-participante';
import { PatrocinadoraService } from '../../common/services/patrocinadora';
import { PercentualRecebimentoRendaService } from '../../common/services/percentual-recebimento-renda';
import { TransformacaoBeneficioService } from '../../common/services/transformacao-beneficio';

export const getPatrocinadora = createAsyncThunk(
  'plan/getPatrocinadora',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const { matricula } = state.user.data;
      const { plano } = state.user.data;

      const patrocinadoraService = new PatrocinadoraService();

      const patrocinadora =
        await patrocinadoraService.getPatrocinadoraParticipante(
          matricula,
          plano
        );

      return patrocinadora.NomePatrocinadora;
    } catch (error) {
      return rejectWithValue('Erro inesperado. Tente novamente mais tarde.');
    }
  }
);

const mapBeneficiariosResponse = (response) => {
  const beneficiarioRaw = response.Beneficiario;

  let beneficiariosArray = [];

  if (Array.isArray(beneficiarioRaw)) {
    beneficiariosArray = beneficiarioRaw;
  } else if (beneficiarioRaw) {
    beneficiariosArray = [beneficiarioRaw];
  }

  return beneficiariosArray.map((beneficiario) => {
    const [codigo, nome] = (beneficiario.CodigoNome || '').split(' - ');

    return {
      codigo: codigo || '',
      nome: nome || '',
      dataNascimento: beneficiario.DataNascimento
        ? beneficiario.DataNascimento.split('-').reverse().join('/')
        : '',
      parentesco: beneficiario.Parentesco,
      situacao: beneficiario.Situacao,
    };
  });
};

export const getBeneficiarios = createAsyncThunk(
  'plan/getBeneficiarios',
  async (dataAtual, { rejectWithValue, getState }) => {
    try {
      const { matricula, plano } = getState().user.data;
      const beneficiariosService = new BeneficiariosService();
      const response = await beneficiariosService.getBeneficiarios(
        matricula,
        plano,
        dataAtual
      );

      const beneficiarios = mapBeneficiariosResponse(response);

      return beneficiarios;
    } catch (error) {
      return rejectWithValue('Erro inesperado. Tente novamente mais tarde.');
    }
  }
);

export const getPerfilInvestimento = createAsyncThunk(
  'plan/getPerfilInvestimento',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const { matricula, plano } = state.user.data;

      const perfilServices = new AlteracaoPerfilService();

      const perfil = await perfilServices.getPerfilAtualParticipante(
        matricula,
        plano
      );

      return perfil;
    } catch (error) {
      return rejectWithValue('Erro inesperado. Tente novamente mais tarde.');
    }
  }
);

export const getDIB = createAsyncThunk(
  'plan/getDIB',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const { matricula, plano } = state.user.data;

      const participanteService = new MemoriaCalculoParticipanteService();

      const { DIB } =
        await participanteService.getMemoriaCalculoBeneficioParticipante(
          matricula,
          plano
        );

      if (DIB) {
        return DIB;
      }

      return '01/11/1997';
    } catch (error) {
      return rejectWithValue('Erro inesperado. Tente novamente mais tarde.');
    }
  }
);

const buildDadosRentabilidade = (dadosRentabilidadeRaw) => ({
  ContaAdicional: dadosRentabilidadeRaw.ContaAdicional,
  ContaIndividual: dadosRentabilidadeRaw.ContaIndividual,
  ContaPatronal: dadosRentabilidadeRaw.ContaPatronal,
  Saldo: dadosRentabilidadeRaw.saldo,
  Rentabilidade: dadosRentabilidadeRaw.rentabilidade,
  evolucaoSaldo: dadosRentabilidadeRaw.evolucaoSaldo,
  ultimaRentabilidade: dadosRentabilidadeRaw.ultimaRentabilidade,
});

const buildSaudeFinanceira = (percentualSaldo, exaurimento, limitesFarol) => {
  const retiradaParticipanteFormatada = Number(
    String(percentualSaldo).replace(',', '.')
  );

  const farolAtual = (limitesFarol || []).find(
    (limite) =>
      Number(percentualSaldo) >= limite.Minimo &&
      Number(percentualSaldo) <= limite.Maximo
  );

  return {
    exaurimento,
    percentualRetirada: retiradaParticipanteFormatada,
    farol: farolAtual,
  };
};

export const getDadosCardInicial = createAsyncThunk(
  'plan/getDadosCardInicial',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const { corteExtrato } = state.configs.dataCorte.data;
      const { matricula, plano } = state.user.data;
      const { dadosPessoais } = state.user;

      const anoMes = `${corteExtrato[1]}${corteExtrato[0]}`;

      const servicesContribuicao = new ExtratoSaldoContaService();

      const dadosRentabilidadeRaw =
        await servicesContribuicao.getExtratoSaldoContaParticipante(
          matricula,
          plano,
          anoMes
        );

      const servicePercentual = new PercentualRecebimentoRendaService();
      const percentualSaldo = await servicePercentual.getRecebimentoSaldoContas(
        matricula,
        plano
      );

      const retiradaParticipanteFormatada = Number(
        String(percentualSaldo).replace(',', '.')
      );

      const calculadoraExaurimentoService = new TransformacaoBeneficioService();

      const exaurimento =
        await calculadoraExaurimentoService.getCalculoExaurimento({
          matricula,
          plano,
          dataNascimento: dadosPessoais.dataNascimento
            .split('/')
            .reverse()
            .join('-'),
          parcelaAdicional: 1,
          saldo: dadosRentabilidadeRaw.saldo,
          saqueVista: 0,
          inflacao: 4,
          renda:
            dadosRentabilidadeRaw.saldo * (retiradaParticipanteFormatada / 100),
        });

      const limitesFarol =
        await calculadoraExaurimentoService.getLimitesSaudeFinanceira();

      const saudeFinanceira = buildSaudeFinanceira(
        percentualSaldo,
        exaurimento,
        limitesFarol
      );

      const dadosRentabilidade = buildDadosRentabilidade(dadosRentabilidadeRaw);

      return { saudeFinanceira, dadosRentabilidade };
    } catch (error) {
      return rejectWithValue('Erro inesperado. Tente novamente mais tarde.');
    }
  }
);
